import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import OpacityOutlinedIcon from '@mui/icons-material/OpacityOutlined';
import WcOutlinedIcon from '@mui/icons-material/WcOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useAuthState } from 'react-firebase-hooks/auth';
import { saveBathroomLogEntry } from '../../services/bathroomLogService';
import {getCalendarDateKey} from '../../utils/calendarDateKey';
import LogFormShell from '../UI/LogFormShell';
import LogDateField from '../UI/LogDateField';
import LogTimeField from '../UI/LogTimeField';
import LogDatePickerPopover from '../UI/LogDatePickerPopover';
import BristolStoolScaleSelector from '../UI/BristolStoolScaleSelector';
import SimpleNotesInput from '../UI/SimpleNotesInput';
import useChildName from '../../hooks/useChildName';
import { auth, db } from '../../services/firebase';
import { BATHROOM_SCALE } from '../../constants/habitTypes';
import { getAdaptiveBathroomLogConfig, resolveAdaptiveBathroomLogPresentation } from './shared/adaptiveBathroomLogConfig';

const pad = (n) => String(n).padStart(2, '0');

const getDefaultDate = () => getCalendarDateKey(new Date());

const getDefaultTime = () => {
  const now = new Date();
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const isBowelMovementType = (value) => value === 'Bowel movement' || value === 'Both';

const bathroomTypeMeta = {
  Urination: {
    iconComponent: OpacityOutlinedIcon,
    bg: '#EFF6FF',
    border: '#BFDBFE',
    ink: '#3A6AA0',
    iconBg: '#BFDBFE',
  },
  'Bowel movement': {
    iconComponent: WcOutlinedIcon,
    bg: '#F6DED2',
    border: '#F6D5C8',
    ink: '#6B3A24',
    iconBg: '#F6D5C8',
  },
  Both: {
    iconComponent: DoneOutlinedIcon,
    bg: '#EAF2EE',
    border: '#C9E4D8',
    ink: '#2F5A49',
    iconBg: '#C9E4D8',
  },
  Accident: {
    iconComponent: WarningAmberOutlinedIcon,
    bg: '#FEF9EC',
    border: '#FDE68A',
    ink: '#92610A',
    iconBg: '#FDE68A',
  },
};

const locationMeta = {
  activeBg: '#6FA594',
  activeInk: '#FFFFFF',
  activeBorder: '#6FA594',
  inactiveBg: '#FFFFFF',
  inactiveInk: '#4A5954',
  inactiveBorder: '#E5ECE8',
};

const bathroomTileStyles = {
  root: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 1.25,
    p: 0,
  },
  tile: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: 1.4,
    py: 1.05,
    minHeight: 74,
    borderRadius: '2px',
    border: '1.5px solid #E5ECE8',
    bgcolor: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 160ms ease',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#D6E8E1',
      transform: 'translateY(-1px)',
    },
  },
};

const bathroomSectionLabel = {
  fontSize: '13px',
  fontWeight: 700,
  color: '#1A2320',
  lineHeight: 1.2,
  mb: 1,
};

const getEntryContextText = (entry = {}) => String(
  entry.note
  || entry.notes
  || entry.sourceText
  || entry.originalData?.sourceText
  || entry.source?.body
  || entry.originalData?.source?.body
  || entry.content
  || entry.originalData?.content
  || entry.text
  || entry.originalData?.text
  || entry.primaryText
  || entry.originalData?.primaryText
  || entry.label
  || entry.originalData?.label
  || entry.titlePrefix
  || entry.originalData?.titlePrefix
  || entry.title
  || entry.originalData?.title
  || ''
).trim();

const BathroomLogSheet = ({ open, onClose, child, mode = 'create', initialData = null, entryId = null }) => {
  const [user] = useAuthState(auth);
  const { childName } = useChildName(child?.id);
  const [bathroomDate, setBathroomDate] = useState(getDefaultDate);
  const [bathroomTime, setBathroomTime] = useState(getDefaultTime);
  const [bathroomType, setBathroomType] = useState(null);
  const [location, setLocation] = useState(null);
  const [consistency, setConsistency] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const bathroomConfig = useMemo(() => getAdaptiveBathroomLogConfig(child), [child]);
  const bathroomPresentation = useMemo(() => resolveAdaptiveBathroomLogPresentation({ child }), [child]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialData) {
      const bathroomDetails = initialData.bathroomDetails || initialData.originalData?.bathroomDetails || initialData.data?.bathroomDetails || {};
      const timestamp = initialData.timestamp?.toDate ? initialData.timestamp.toDate() : (initialData.timestamp ? new Date(initialData.timestamp) : null);
      const nextDate = initialData.localDate || bathroomDetails.localDate || (timestamp instanceof Date && !Number.isNaN(timestamp.getTime())
        ? [
            timestamp.getFullYear(),
            String(timestamp.getMonth() + 1).padStart(2, '0'),
            String(timestamp.getDate()).padStart(2, '0'),
          ].join('-')
        : getDefaultDate());
      const nextTime = initialData.localTime || bathroomDetails.localTime || (timestamp instanceof Date && !Number.isNaN(timestamp.getTime())
        ? timestamp.toTimeString().slice(0, 5)
        : getDefaultTime());

      setBathroomDate(nextDate);
      setBathroomTime(nextTime);
      setBathroomType(bathroomDetails.type || initialData.bathroomType || initialData.type || initialData.originalData?.bathroomType || null);
      setLocation(bathroomDetails.location || initialData.location || initialData.originalData?.location || null);
      setConsistency(bathroomDetails.consistency || initialData.consistency || initialData.originalData?.consistency || null);
      setNotes(bathroomDetails.notes || initialData.notes || initialData.originalData?.notes || getEntryContextText(initialData));
      return;
    }
    setBathroomDate(getDefaultDate());
    setBathroomTime(getDefaultTime());
    setBathroomType(null);
    setLocation(null);
    setConsistency(null);
    setNotes('');
  }, [open, child?.id, initialData, mode]);

  useEffect(() => {
    if (!isBowelMovementType(bathroomType)) {
      setConsistency(null);
    }
  }, [bathroomType]);

  const resolvedChildName = childName || child?.name || 'Child';

  const bathroomTimestamp = useMemo(() => {
    const timestamp = new Date(`${bathroomDate}T${bathroomTime}`);
    return Number.isNaN(timestamp.getTime()) ? new Date() : timestamp;
  }, [bathroomDate, bathroomTime]);

  const bathroomDateObject = useMemo(() => {
    const parsed = new Date(`${bathroomDate}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [bathroomDate]);

  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (nextDate) => {
    if (!nextDate || Number.isNaN(nextDate.getTime())) {
      return;
    }

    setBathroomDate([
      nextDate.getFullYear(),
      String(nextDate.getMonth() + 1).padStart(2, '0'),
      String(nextDate.getDate()).padStart(2, '0'),
    ].join('-'));
    handleDatePickerClose();
  };

  const getTypeLabel = (value) => (
    bathroomConfig.typeOptions.find((option) => option.value === value)?.label || value
  );

  const getLocationLabel = (value) => (
    bathroomConfig.locationOptions.find((option) => option.value === value)?.label || value
  );

  const renderLocationChip = (option) => {
    const selected = location === option.value;
    return (
      <Chip
        key={option.value}
        label={option.label}
        onClick={() => setLocation(option.value)}
        variant="outlined"
        sx={{
          height: 38,
          borderRadius: 999,
          px: 0.5,
          fontSize: '0.9rem',
          fontWeight: 600,
          borderWidth: '1.5px',
          borderStyle: 'solid',
          borderColor: selected ? locationMeta.activeBorder : locationMeta.inactiveBorder,
          bgcolor: selected ? locationMeta.activeBg : locationMeta.inactiveBg,
          color: selected ? locationMeta.activeInk : locationMeta.inactiveInk,
          '& .MuiChip-label': {
            px: 1.25,
          },
        }}
      />
    );
  };

  const renderTypeCard = (option) => {
    const selected = bathroomType === option.value;
    const meta = bathroomTypeMeta[option.value] || bathroomTypeMeta.Accident;
    return (
      <ButtonBase
        key={option.value}
        onClick={() => setBathroomType(option.value)}
        sx={{
          ...bathroomTileStyles.tile,
          borderColor: selected ? meta.border : '#E5ECE8',
          bgcolor: selected ? meta.bg : '#FFFFFF',
          boxShadow: selected ? `0 0 0 1px ${meta.border} inset` : 'none',
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 10,
            bgcolor: selected ? meta.iconBg : '#EEF2F0',
            color: selected ? meta.ink : '#7A8A84',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          fontSize: '1rem',
        }}
      >
        {meta.iconComponent ? <meta.iconComponent sx={{ fontSize: 18, color: selected ? meta.ink : '#7A8A84' }} /> : null}
      </Box>
        <Box sx={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>
          <Typography
            sx={{
              fontSize: '13.5px',
              fontWeight: selected ? 700 : 600,
              color: selected ? meta.ink : '#4A5954',
              lineHeight: 1.2,
            }}
          >
            {option.label}
          </Typography>
        </Box>
        {selected ? (
          <CheckRoundedIcon sx={{ fontSize: 18, color: meta.ink, flexShrink: 0 }} />
        ) : null}
      </ButtonBase>
    );
  };

  const showConsistencyScale = !bathroomType || isBowelMovementType(bathroomType);
  const handleConsistencyChange = (nextValue) => {
    if (!isBowelMovementType(bathroomType)) {
      setBathroomType('Bowel movement');
    }
    setConsistency(nextValue);
  };

  const handleSave = async () => {
    if (!child?.id || !user?.uid) return;

    const summary = [
      getTypeLabel(bathroomType),
      location ? `at ${getLocationLabel(location)}` : null,
      isBowelMovementType(bathroomType) && consistency
        ? `Consistency: ${BATHROOM_SCALE[consistency]?.label || consistency}`
        : null,
    ].filter(Boolean).join(' — ');

    setSaving(true);
    try {
      const docRef = await saveBathroomLogEntry({
        child, user, bathroomDate, bathroomTimestamp, bathroomType, location,
        notes, summary, bathroomPresentation, mode, entryId, initialData,
        consistency: isBowelMovementType(bathroomType) ? consistency : null,
        consistencyLabel: isBowelMovementType(bathroomType)
          ? BATHROOM_SCALE[consistency]?.label || null
          : null,
      });

      window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
        detail: {
          id: docRef.id,
          collection: 'dailyLogs',
          childId: child.id,
        },
      }));

      window.dispatchEvent(new CustomEvent('captureez:timeline-focus-date', {
        detail: {
          childId: child.id,
          date: bathroomTimestamp.toISOString(),
        },
      }));

      onClose?.();
    } catch (error) {
      console.error('Error saving bathroom log:', error);
    } finally {
      setSaving(false);
    }
  };

  const formBody = (
    <Stack spacing={2}>
        <Box
          sx={{
            position: 'relative',
            p: 0,
            borderRadius: 0,
            bgcolor: 'transparent',
            border: 'none',
          }}
        >
          <Stack direction="row" spacing={1.25} sx={{ width: '100%' }}>
            <LogDateField
              value={bathroomDateObject}
              onClick={handleDatePickerOpen}
              containerSx={{ flex: 1, minWidth: 0 }}
            />

            <LogTimeField
              value={bathroomTime}
              onChange={(event) => setBathroomTime(event.target.value)}
              containerSx={{ flex: 1, minWidth: 0 }}
            />
          </Stack>

          <LogDatePickerPopover
            open={Boolean(datePickerAnchor)}
            anchorEl={datePickerAnchor}
            onClose={handleDatePickerClose}
            value={bathroomDateObject}
            onChange={handleDateChange}
          />
        </Box>

        <Box>
          <Typography sx={bathroomSectionLabel}>
            {bathroomConfig.eventSectionLabel}
          </Typography>
          <Box sx={bathroomTileStyles.root}>
            {bathroomConfig.typeOptions.map(renderTypeCard)}
          </Box>
        </Box>

        <Box>
          <Typography sx={bathroomSectionLabel}>
            {bathroomConfig.locationSectionLabel}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {bathroomConfig.locationOptions.map(renderLocationChip)}
          </Box>
        </Box>

        {showConsistencyScale ? (
          <Box>
            <BristolStoolScaleSelector
              value={consistency}
              onChange={handleConsistencyChange}
              label={bathroomConfig.stoolScaleLabel}
              unselectedHint={bathroomConfig.stoolScaleHint}
            />
          </Box>
        ) : null}

        <SimpleNotesInput
          label={bathroomConfig.notesLabel}
          hintText="Tip: use your phone mic to dictate notes."
          value={notes}
          onDataChange={(data) => setNotes(data?.text || '')}
          placeholder={bathroomConfig.notesPlaceholder}
          minRows={3}
        />
      </Stack>
  );

  const footerActions = (
    <Box
      sx={{
        position: 'relative',
        px: 0,
        pt: 1,
        pb: 'max(12px, env(safe-area-inset-bottom))',
        bgcolor: 'transparent',
        backdropFilter: 'none',
        borderTop: 'none',
      }}
    >
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || !child?.id || !user?.uid || !bathroomType}
        startIcon={!saving && child?.id && user?.uid && bathroomType ? <CheckRoundedIcon /> : null}
        sx={{
          width: '100%',
          height: 54,
          borderRadius: '999px',
          bgcolor: bathroomType ? '#6FA594' : '#E5ECE8',
          color: bathroomType ? '#FFFFFF' : '#7A8A84',
          fontSize: '16px',
          fontWeight: 700,
          textTransform: 'none',
          boxShadow: bathroomType ? '0 4px 14px rgba(111, 165, 148, 0.3)' : 'none',
          '&:hover': {
            bgcolor: bathroomType ? '#5F9A89' : '#E5ECE8',
            boxShadow: bathroomType ? '0 4px 14px rgba(111, 165, 148, 0.3)' : 'none',
          },
          '&.Mui-disabled': {
            bgcolor: '#E5ECE8',
            color: '#7A8A84',
            boxShadow: 'none',
          },
        }}
      >
        {saving ? 'Saving...' : bathroomConfig.saveLabel}
      </Button>
    </Box>
  );

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={bathroomConfig.title}
      titleBadge={resolvedChildName}
      subtitle={bathroomConfig.subtitle}
      compactTitle
      titleWeight={700}
      titleSx={{
        fontSize: '22px',
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
        color: '#1A2320',
      }}
      subtitleSx={{
        mt: 0.65,
        fontSize: '13px',
        color: '#7A8A84',
      }}
      titleBadgeSx={{
        height: 28,
        bgcolor: '#E8EDC8',
        color: '#4C521F',
        border: '1px solid #E8EDC8',
        fontSize: '11.5px',
        fontWeight: 600,
      }}
      closeButtonSx={{
        width: 32,
        height: 32,
        bgcolor: '#EEF2F0',
        borderColor: '#EEF2F0',
        '&:hover': {
          bgcolor: '#EEF2F0',
        },
      }}
      closeIconSx={{
        color: '#7A8A84',
        fontSize: 18,
      }}
      surfaceSx={{
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        height: 'min(92dvh, 920px)',
        maxHeight: 'min(92dvh, 920px)',
      }}
      bodySx={{
        px: { xs: 1.25, sm: 4 },
        pt: 1.25,
        pb: 2,
      }}
      footer={footerActions}
      mobileBreakpoint="md"
      maxWidth="sm"
    >
      {formBody}
    </LogFormShell>
  );
};

export default BathroomLogSheet;
